import React, {useState} from 'react';
import {Box, Button, InputAdornment, MenuItem, TextField, Tooltip, Typography} from '@mui/material';
import {Add, Info} from '@mui/icons-material';
import {Controller} from 'react-hook-form';
import {FlowSection} from '../flows/FlowDesign';
import WorkspaceDropdown from '../workspace/WorkspaceDropdown';
import CountrySelect from '../utils/CountrySelect';
import ReactHookTagsSelect from '../tags/ReactHookTagsSelect';
import CurrencySelect from '../offers/CurrencySelect';
import TrafficSourcePopUp from '../traffic-sources/TrafficSourcePopUp';
import TrafficSourceSelect from './TrafficSourceSelect';
import TrackingDomainSelect from './TrackingDomainSelect';
import ConversionEventSelect from './ConversionEventSelect';
import ConversionPixelSelect from './ConversionPixelSelect';

export const campaignCostModels = [
    ['auto', 'Automatic', 'Import costs from your traffic source parameters or connected integration.'],
    ['cpc', 'Cost per click (CPC)', 'Set a fixed cost for each click.'],
    ['cpa', 'Cost per conversion (CPA)', 'Set a fixed cost for each conversion.'],
    ['cps', 'Revenue share (CPS)', 'Set the percentage of revenue paid to your traffic source.'],
    ['cpm', 'Cost per thousand visits (CPM)', 'Use the cost per 1,000 visits passed by your traffic source.'],
    ['notTrack', 'Do not track', 'You can still update campaign costs manually in Ads Manager.']
];

const integrationNames = {google: 'Google Ads', microsoft: 'Microsoft Ads', taboola: 'Taboola',
    facebook: 'Facebook (Meta)', tiktok: 'TikTok', mgid: 'MGID', outbrain: 'Outbrain',
    outbrain_dsp: 'Outbrain DSP', newsbreak: 'NewsBreak', smartnews: 'SmartNews', mediago: 'MediaGo'};

export function supportsCampaignConversions(integration) {
    const prefix = {tiktok: 'tt', newsbreak: 'nb'}[integration?.networkType] || integration?.networkType;
    return !!integrationNames[integration?.networkType] && integration.integrationCredentials?.some(item =>
        [prefix + '_enable_conversion_api', integration.networkType + '_enable_conversion_api'].includes(item.keyName) && item.keyValue === 'true');
}

export default function CampaignSetup({form, workspaceId, onTrafficSourceChange, integration}) {
    const {control, register, watch, setValue, getValues, formState: {errors}} = form;
    const [createTraffic, setCreateTraffic] = useState(false);
    const costModel = watch('costModel');
    return <Box className="campaign-surface-inputs" sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))'}, gap: 2, alignItems: 'start'}}>
        <FlowSection title="Campaign details" description="Give your campaign a name and choose where it belongs.">
            <TextField {...register('name', {required: 'Enter a campaign name.', validate: value => (value?.trim().length >= 3) || 'Enter a campaign name with at least 3 characters.', maxLength: {value: 255, message: 'Use at most 255 characters.'}})}
                label="Campaign name *" placeholder="e.g. Summer promotion" size="small" fullWidth
                error={!!errors.name} helperText={errors.name?.message} slotProps={{inputLabel: {shrink: true}}}/>
            <WorkspaceDropdown control={control} workspaceId={workspaceId} setValue={setValue} validate={value => value?.value > 0 || 'Please select a workspace.'}/>

            <Box display="flex" flexDirection="row" flexWrap="noWrap">
                <Box flexGrow={1}>
                    <CountrySelect control={control} compact/>
                </Box>
                <Box sx={{pt: 1, pr: 1}}>
                    <Tooltip
                        disableFocusListener
                        sx={{
                            marginLeft: 2,
                            fontSize: "18px",
                            position: "relative",
                            cursor: "pointer"
                        }}
                        title="Country organizes your campaigns. It does not restrict incoming traffic.">
                        <Info color={"primary"} fontSize={"small"}/>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{minWidth: 0, '& .MuiAutocomplete-root > .MuiBox-root': {alignItems: 'center'},
                '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:first-of-type': {minWidth: 0},
                '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:last-child': {py: 0, '& > div': {paddingTop: '0 !important'}}}}>
                <ReactHookTagsSelect control={control} getValues={getValues}/>
            </Box>
            <Box sx={{borderRadius: '8px', px: 1.5, py: 1, bgcolor: 'background.paper', minWidth: 0}}>
                <Typography sx={{fontSize: 11, color: 'text.secondary', mb: 0.25}}>Campaign name preview</Typography>
                <Typography sx={{fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere'}}>
                    {watch('country')?.label || 'Global'} – {watch('trafficSource')?.label || 'Traffic source'} – {watch('name') || 'Campaign name'}
                </Typography>
            </Box>
        </FlowSection>
        <Box sx={{display: 'grid', gap: 2, minWidth: 0}}>
            <FlowSection
                title="Traffic Source"
                description="Choose your traffic source for campaign link."
                action={
                    <Button
                        type="button"
                        size="small"
                        startIcon={<Add/>}
                        aria-label="Create traffic source"
                        onClick={() => {
                            setCreateTraffic(true);
                        }}
                        sx={{whiteSpace: 'nowrap'}}
                    >
                        Create
                    </Button>
                }
            >
                <TrafficSourceSelect control={control} showFormLabel={false} showInputLabel updateFunc={onTrafficSourceChange}
                    validate={value => value?.value > 0 || 'Please select a traffic source.'}/>

                {supportsCampaignConversions(integration) && <>
                    <Typography sx={{fontSize: 13, fontWeight: 600, color: 'primary.main'}}>
                        {integrationNames[integration.networkType]} Conversion API <Box component="span" sx={{color: 'text.secondary', fontWeight: 400}}>(optional)</Box>
                    </Typography>
                    <ConversionEventSelect control={control} errors={errors} options={integration.integrationEvents || []}/>
                    {['facebook', 'tiktok', 'smartnews'].includes(integration.networkType) &&
                        <ConversionPixelSelect control={control} errors={errors} options={integration.integrationPixels || []}/>}
                </>}

            </FlowSection>

            <FlowSection title="Tracking domain" description="Choose your domain for campaign link.">
                <TrackingDomainSelect compact control={control} errors={errors} setValue={setValue} watch={watch}/>
            </FlowSection>

            <FlowSection title="Cost tracking" description="Define how campaign spend is recorded.">
                <Controller name="costModel" control={control} rules={{required: 'Please select a cost model.', validate: value => campaignCostModels.some(model => model[0] === value) || 'Please select a cost model.'}} render={({field, fieldState}) =>
                    <TextField {...field} select label="Cost model *" size="small" fullWidth error={!!fieldState.error} helperText={fieldState.error?.message} slotProps={{inputLabel: {shrink: true}}}>
                        {campaignCostModels.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>}/>
                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: -1}}>{campaignCostModels.find(([value]) => value === costModel)?.[2]}</Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: ['cpc', 'cpa', 'cps'].includes(costModel) ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)'}, gap: 2}}>
                    <CurrencySelect control={control} required/>
                    {['cpc', 'cpa', 'cps'].includes(costModel) && <TextField {...register('costValue', {
                        validate: value => (/^\d+(\.\d{1,4})?$/.test(value) && (costModel !== 'cps' || (Number(value) > 0 && Number(value) <= 100))) ||
                            (costModel === 'cps' ? 'Enter a percentage above 0 and up to 100.' : 'Enter a positive cost with up to 4 decimal places.')
                    })} size="small" fullWidth label={costModel === 'cps' ? 'Revenue share *' : 'Cost value *'} placeholder={costModel === 'cps' ? '75' : '0.50'}
                        error={!!errors.costValue} helperText={errors.costValue?.message}
                        slotProps={{inputLabel: {shrink: true}, htmlInput: {inputMode: 'decimal'}, input: costModel === 'cps' ? {endAdornment: <InputAdornment position="end">%</InputAdornment>} : undefined}}/>}
                </Box>
            </FlowSection>
        </Box>
        {createTraffic && <TrafficSourcePopUp open onClose={() => setCreateTraffic(false)}/>}
    </Box>;
}
